import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { usePlatformAuth } from '../../context/PlatformAuthContext';
import { isSuperAdminOrOwner, staffRoleLabel } from '../../lib/staffRoles';
import { useSystemBarsStyle } from '../../native/systemBars';
import '../layout/PlatformLayout.css';
import {
  LayoutDashboard,
  Users,
  Building2,
  Settings,
  LogOut,
  Menu,
  X,
  Zap,
  BarChart2,
  LifeBuoy,
  Bell,
  Map,
  Bug,
  Home,
  Megaphone
} from 'lucide-react';

export default function PlatformLayout({ children }) {
  // Charcoal header under the status bar → light status-bar icons (native only, B-33).
  useSystemBarsStyle('DARK');
  const { platformUser, logout } = usePlatformAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Start closed on mobile so content is immediately visible
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.innerWidth > 768;
  });

  // Track whether we're on mobile for overlay behaviour
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 768;
  });

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      // Auto-open on desktop, auto-close doesn't reset open state
      if (!mobile) setSidebarOpen(true);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/marakin-admin/login');
  };

  const isActive = (path) => location.pathname === path;

  // On mobile, close sidebar after navigating
  const handleNavClick = () => {
    if (isMobile) setSidebarOpen(false);
  };

  return (
    <div className="platform-layout">
      {/* Overlay — closes sidebar when tapped on mobile */}
      {isMobile && sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`platform-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <Link to="/marakin-admin" className="sidebar-brand" onClick={handleNavClick}>
            <img src="/marakin-logo-cream.png" alt="Marakin" />
          </Link>
          <button
            className="sidebar-toggle-mobile"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="sidebar-nav">
          <Link
            to="/marakin-admin"
            className={`nav-item ${isActive('/marakin-admin') ? 'active' : ''}`}
            onClick={handleNavClick}
          >
            <LayoutDashboard size={20} />
            <span>Dashboard</span>
          </Link>

          <Link
            to="/marakin-admin/clubs"
            className={`nav-item ${isActive('/marakin-admin/clubs') ? 'active' : ''}`}
            onClick={handleNavClick}
          >
            <Building2 size={20} />
            <span>Clubs</span>
          </Link>

          <Link
            to="/marakin-admin/sponsors"
            className={`nav-item ${isActive('/marakin-admin/sponsors') ? 'active' : ''}`}
            onClick={handleNavClick}
          >
            <Zap size={20} />
            <span>Sponsors</span>
          </Link>

          {/* B-50 Phase 0: staff-only sponsor ad prototype. */}
          <Link
            to="/marakin-admin/ad-preview"
            className={`nav-item ${isActive('/marakin-admin/ad-preview') ? 'active' : ''}`}
            onClick={handleNavClick}
          >
            <Megaphone size={20} />
            <span>Ad preview</span>
          </Link>

          <Link
            to="/marakin-admin/reports"
            className={`nav-item ${isActive('/marakin-admin/reports') ? 'active' : ''}`}
            onClick={handleNavClick}
          >
            <BarChart2 size={20} />
            <span>Reports</span>
          </Link>

          <Link
            to="/marakin-admin/support"
            className={`nav-item ${isActive('/marakin-admin/support') ? 'active' : ''}`}
            onClick={handleNavClick}
          >
            <LifeBuoy size={20} />
            <span>Support</span>
          </Link>

          <Link
            to="/marakin-admin/push"
            className={`nav-item ${isActive('/marakin-admin/push') ? 'active' : ''}`}
            onClick={handleNavClick}
          >
            <Bell size={20} />
            <span>Push log</span>
          </Link>

          <Link
            to="/marakin-admin/roadmap"
            className={`nav-item ${isActive('/marakin-admin/roadmap') ? 'active' : ''}`}
            onClick={handleNavClick}
          >
            <Map size={20} />
            <span>Roadmap</span>
          </Link>

          <Link
            to="/marakin-admin/bugs-tweaks"
            className={`nav-item ${isActive('/marakin-admin/bugs-tweaks') ? 'active' : ''}`}
            onClick={handleNavClick}
          >
            <Bug size={20} />
            <span>Bugs &amp; Tweaks</span>
          </Link>

          {isSuperAdminOrOwner(platformUser?.role) && (
            <Link
              to="/marakin-admin/staff"
              className={`nav-item ${isActive('/marakin-admin/staff') ? 'active' : ''}`}
              onClick={handleNavClick}
            >
              <Users size={20} />
              <span>Staff</span>
            </Link>
          )}

          <Link
            to="/marakin-admin/settings"
            className={`nav-item ${isActive('/marakin-admin/settings') ? 'active' : ''}`}
            onClick={handleNavClick}
          >
            <Settings size={20} />
            <span>Settings</span>
          </Link>

          {/* SB-8: the app has no address bar — give staff a way back to the clubs. */}
          <Link to="/my-clubs" className="nav-item" onClick={handleNavClick}>
            <Home size={20} />
            <span>Back to the app</span>
          </Link>
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">
              {platformUser?.firstName?.[0]}{platformUser?.lastName?.[0]}
            </div>
            <div className="user-details">
              <p className="user-name">
                {platformUser?.firstName} {platformUser?.lastName}
              </p>
              <p className="user-role">{staffRoleLabel(platformUser?.role)}</p>
            </div>
          </div>
          <button className="btn-logout" onClick={handleLogout} title="Logout">
            <LogOut size={18} />
          </button>
        </div>
      </aside>

      <div className="platform-main">
        <header className="platform-header">
          <button
            className="sidebar-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label="Toggle menu"
          >
            {sidebarOpen && !isMobile ? <X size={24} /> : <Menu size={24} />}
          </button>
          <div className="header-spacer" />
          <div className="header-user">
            <span>{platformUser?.firstName}</span>
            <button className="btn-logout-header" onClick={handleLogout}>
              <LogOut size={18} />
            </button>
          </div>
        </header>

        <main className="platform-content">
          {children}
        </main>
      </div>
    </div>
  );
}
